/**
 * Outlet selection.
 *
 * "All outlets" is the canonical aggregate (matches original
 * buyer.zeemart.co's default) and is represented internally as the
 * comma-joined string of every outletId the session can act for.
 * Reports endpoints accept the comma-list verbatim.
 *
 * A single outletId is also legal (drilldown into one location).
 *
 * Selection state is event-bussed via localStorage + a CustomEvent so
 * every surface (topbar picker, dashboard fetcher, Aiko stream context)
 * stays in sync without React context plumbing.
 */
import { useEffect, useState } from "react";

const KEY = "zeemartasi.outletId";
const EVT = "outlet-changed";

export const ALL_OUTLETS_SENTINEL = "all-outlets";

export function getOutletId(): string | null {
  try { return localStorage.getItem(KEY); } catch { return null; }
}

export function setOutletId(id: string) {
  try {
    localStorage.setItem(KEY, id);
    window.dispatchEvent(new CustomEvent(EVT, { detail: id }));
  } catch { /* ignore */ }
}

/** What we actually pass to the proxy when the user picked the sentinel. */
export function resolveOutletParam(
  selected: string | null,
  outlets: ReadonlyArray<{ outletId: string }>,
): string | null {
  if (!selected) return null;
  if (selected === ALL_OUTLETS_SENTINEL) {
    return outlets.map((o) => o.outletId).join(",") || null;
  }
  return selected;
}

export function useOutletId(fallback?: string | null): string | null {
  const [id, setId] = useState<string | null>(() => getOutletId() ?? fallback ?? null);
  useEffect(() => {
    const onChange = (e: Event) => setId((e as CustomEvent<string>).detail ?? getOutletId());
    window.addEventListener(EVT, onChange);
    return () => window.removeEventListener(EVT, onChange);
  }, []);
  useEffect(() => {
    if (!id && fallback) {
      setOutletId(fallback);
      setId(fallback);
    }
  }, [id, fallback]);
  return id;
}
